import React, { FC, useEffect } from 'react';
import { Button, Input, message } from 'antd';
import { RouteComponentProps, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Path } from 'path-parser';

import { Form } from '~/components';
import { passwordResetTokenExpired, passwordResetSuccess, passwordResetOtherError } from '~/routes';

import { useCheckToken, usePasswordReset } from '~/hooks/passwordReset';
import PasswordResetLayout from '~/layout/PasswordResetLayout';

const path = new Path('/:route?token');

const PasswordReset: FC<RouteComponentProps> = () => {
  const { t } = useTranslation('passwordReset');
  const history = useHistory();
  const location = useLocation();
  const { token } = path.partialTest(location.pathname + location.search) || {};

  const checkTokenMutation = useCheckToken({
    onError(error) {
      const status = error?.response?.status.toString();
      if (status === '404') {
        history.replace(`/${passwordResetTokenExpired}`);
      } else {
        history.push(`/${passwordResetOtherError}?token=${token}`);
      }
    },
  });
  const passwordResetMutation = usePasswordReset();

  useEffect(() => {
    if (token) {
      checkTokenMutation.mutate(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (values: any) => {
    const { newPassword } = values;
    if (token && newPassword) {
      try {
        await checkTokenMutation.mutateAsync(token);
        await passwordResetMutation.mutateAsync({ token, newPassword });
        history.push(`/${passwordResetSuccess}`);
      } catch (error: any) {
        history.push(`/${passwordResetTokenExpired}`);
      }
    } else {
      void message.error('Something went wrong');
    }
  };

  const [form] = Form.useForm();

  return (
    <PasswordResetLayout title={t('Change password')}>
      <Form name="passwordResetForm" form={form} layout="vertical" size="large" onFinish={submit}>
        <Form.Item name="newPassword" rules={[{ required: true, min: 8 }]}>
          <Input.Password
            placeholder={t('New password')}
            disabled={passwordResetMutation.isLoading}
          />
        </Form.Item>
        <Form.Item
          name="newPasswordAgain"
          dependencies={['newPassword']}
          rules={[
            {
              required: true,
            },
            ({ getFieldValue }: any) =>
              ({
                validator(rule: string, value: any) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }

                  // eslint-disable-next-line prefer-promise-reject-errors
                  return Promise.reject(t('translation:passwordsNotMatch'));
                },
              } as any),
          ]}
        >
          <Input.Password
            placeholder={t('Confirm new password')}
            disabled={passwordResetMutation.isLoading}
          />
        </Form.Item>
      </Form>
      <Button
        type="primary"
        size="large"
        htmlType="submit"
        form="passwordResetForm"
        loading={passwordResetMutation.isLoading}
      >
        {t('Confirm')}
      </Button>
    </PasswordResetLayout>
  );
};

export default PasswordReset;
